import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '~/components/ui/sheet';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { MultiSelect } from '~/components/multi-select';
import { SegmentedControl } from '~/components/segmented-control';
import { DateInput } from '~/components/date-input';
import { categoriesApi } from '~/lib/api/client';
import { useProductAvailabilityStatuses, useProductStatuses, useProductSubTypes, type LookupOption } from '~/hooks/use-product-lookups';
import { EMPTY_FILTERS, type ProductFilters } from '~/lib/products/product-filters';

interface FiltersSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Currently-applied filters (the sheet seeds its draft from these on open). */
	value: ProductFilters;
	/** Commit the draft (also resets to page 1 in the caller). */
	onApply: (next: ProductFilters) => void;
	/** Clear all filters. */
	onClear: () => void;
}

type CategoryOption = { id: number; label: string };

/** A labelled filter section. */
function Section({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="flex flex-col gap-1.5">
			<Label className="text-[11px] font-medium uppercase tracking-[0.04em] text-foreground-subtle">{label}</Label>
			{children}
		</div>
	);
}

/** ISO yyyy-MM-dd ↔ Date helpers for the release-date range. */
function isoToDate(iso: string | null): Date | null {
	if (!iso) return null;
	const d = new Date(`${iso}T00:00:00`);
	return Number.isNaN(d.getTime()) ? null : d;
}
function dateToIso(d: Date | null): string | null {
	if (!d) return null;
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${y}-${m}-${day}`;
}

/** Parses a number input into `number | null` (empty → null). */
function parseNum(raw: string): number | null {
	const trimmed = raw.trim();
	if (trimmed === '') return null;
	const n = Number(trimmed);
	return Number.isFinite(n) ? n : null;
}

/**
 * Advanced product-filters side-sheet. Edits a local draft seeded from the applied filters;
 * commits only on Apply. Lookups (statuses / sub-types / availability) come from cached
 * TanStack Query hooks; categories use the async `categoriesApi.lookup` search (mirrors the
 * legacy `ProductCategoriesSelect`). Maps onto `getAllGrid` loadOptions via `applyFilters`.
 */
export function FiltersSheet({ open, onOpenChange, value, onApply, onClear }: FiltersSheetProps) {
	const { t } = useTranslation();
	const [draft, setDraft] = useState<ProductFilters>(value);

	// Re-seed the draft whenever the sheet opens (or the applied value changes while open).
	useEffect(() => {
		if (open) setDraft(value);
	}, [open, value]);

	const statuses = useProductStatuses();
	const subTypes = useProductSubTypes();
	const availability = useProductAvailabilityStatuses();

	const set = <K extends keyof ProductFilters>(key: K, next: ProductFilters[K]) => setDraft(prev => ({ ...prev, [key]: next }));

	// Tri-state segmented options ("Any" / "Yes" / "No"). Encoded as string for the control.
	const triValue = (v: 1 | 2 | null): 'any' | 'yes' | 'no' => (v === 1 ? 'yes' : v === 2 ? 'no' : 'any');
	const triOptions = [
		{ value: 'any' as const, label: t('products.filters.triAny') },
		{ value: 'yes' as const, label: t('common.yes') },
		{ value: 'no' as const, label: t('common.no') }
	];
	const setTri = (key: 'isVisible' | 'hasImage', v: 'any' | 'yes' | 'no') => set(key, v === 'yes' ? 1 : v === 'no' ? 2 : null);

	// Category async search → categoriesApi.lookup (label/id), mirrors legacy ProductCategoriesSelect.
	const loadCategories = async (query: string): Promise<CategoryOption[]> => {
		const res = await categoriesApi.lookup(query, 20);
		return (res.data ?? []).filter(c => c.id != null).map(c => ({ id: c.id as number, label: c.nm ?? '' }));
	};

	const lookupSelected = (all: LookupOption[], ids: number[]): LookupOption[] => {
		const byId = new Map(all.map(o => [o.id, o]));
		return ids.map(id => byId.get(id) ?? { id, label: String(id) });
	};

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-md">
				<SheetHeader className="border-b border-border">
					<SheetTitle>{t('products.filters.title')}</SheetTitle>
					<SheetDescription>{t('products.filters.subtitle')}</SheetDescription>
				</SheetHeader>

				<div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
					{/* Barcode */}
					<Section label={t('products.grid.columns.barcode')}>
						<Input
							value={draft.barcode}
							onChange={e => set('barcode', e.target.value)}
							placeholder={t('products.filters.barcodePlaceholder')}
						/>
					</Section>

					{/* Categories (async lookup) */}
					<Section label={t('products.grid.columns.category')}>
						<MultiSelect<CategoryOption>
							value={draft.categories}
							onChange={next => set('categories', next)}
							options={[]}
							loadOptions={loadCategories}
							getKey={c => String(c.id)}
							getLabel={c => c.label}
							placeholder={t('products.filters.categoriesPlaceholder')}
						/>
					</Section>

					{/* Statuses */}
					<Section label={t('products.filters.statuses')}>
						<MultiSelect<LookupOption>
							value={lookupSelected(statuses.data ?? [], draft.statuses)}
							onChange={next =>
								set(
									'statuses',
									next.map(o => o.id)
								)
							}
							options={statuses.data ?? []}
							getKey={o => String(o.id)}
							getLabel={o => o.label}
							loading={statuses.isLoading}
							placeholder={t('products.filters.statusesPlaceholder')}
						/>
					</Section>

					{/* Sub-types */}
					<Section label={t('products.filters.subTypes')}>
						<MultiSelect<LookupOption>
							value={lookupSelected(subTypes.data ?? [], draft.subTypes)}
							onChange={next =>
								set(
									'subTypes',
									next.map(o => o.id)
								)
							}
							options={subTypes.data ?? []}
							getKey={o => String(o.id)}
							getLabel={o => o.label}
							loading={subTypes.isLoading}
							placeholder={t('products.filters.subTypesPlaceholder')}
						/>
					</Section>

					{/* Availability (enum names) */}
					<Section label={t('products.filters.availability')}>
						<MultiSelect<{ name: string; label: string }>
							value={draft.availability.map(name => ({ name, label: (availability.data ?? []).find(a => a.name === name)?.label ?? name }))}
							onChange={next =>
								set(
									'availability',
									next.map(o => o.name)
								)
							}
							options={availability.data ?? []}
							getKey={o => o.name}
							getLabel={o => o.label}
							loading={availability.isLoading}
							placeholder={t('products.filters.availabilityPlaceholder')}
						/>
					</Section>

					{/* Visibility (tri-state) */}
					<Section label={t('products.grid.columns.visible')}>
						<SegmentedControl
							value={triValue(draft.isVisible)}
							onValueChange={v => setTri('isVisible', v)}
							options={triOptions}
							size="sm"
						/>
					</Section>

					{/* Has image (tri-state) */}
					<Section label={t('products.grid.columns.image')}>
						<SegmentedControl value={triValue(draft.hasImage)} onValueChange={v => setTri('hasImage', v)} options={triOptions} size="sm" />
					</Section>

					{/* Price range */}
					<Section label={t('products.filters.priceRange')}>
						<div className="grid grid-cols-2 gap-2">
							<Input
								type="number"
								inputMode="decimal"
								value={draft.priceFrom ?? ''}
								onChange={e => set('priceFrom', parseNum(e.target.value))}
								placeholder={t('products.filters.from')}
								aria-label={`${t('products.filters.priceRange')} ${t('products.filters.from')}`}
							/>
							<Input
								type="number"
								inputMode="decimal"
								value={draft.priceTo ?? ''}
								onChange={e => set('priceTo', parseNum(e.target.value))}
								placeholder={t('products.filters.to')}
								aria-label={`${t('products.filters.priceRange')} ${t('products.filters.to')}`}
							/>
						</div>
					</Section>

					{/* Discount range */}
					<Section label={t('products.filters.discountRange')}>
						<div className="grid grid-cols-2 gap-2">
							<Input
								type="number"
								value={draft.discountFrom ?? ''}
								onChange={e => set('discountFrom', parseNum(e.target.value))}
								placeholder={t('products.filters.from')}
								aria-label={`${t('products.filters.discountRange')} ${t('products.filters.from')}`}
							/>
							<Input
								type="number"
								value={draft.discountTo ?? ''}
								onChange={e => set('discountTo', parseNum(e.target.value))}
								placeholder={t('products.filters.to')}
								aria-label={`${t('products.filters.discountRange')} ${t('products.filters.to')}`}
							/>
						</div>
					</Section>

					{/* Quantity range */}
					<Section label={t('products.filters.quantityRange')}>
						<div className="grid grid-cols-2 gap-2">
							<Input
								type="number"
								value={draft.quantityFrom ?? ''}
								onChange={e => set('quantityFrom', parseNum(e.target.value))}
								placeholder={t('products.filters.from')}
								aria-label={`${t('products.filters.quantityRange')} ${t('products.filters.from')}`}
							/>
							<Input
								type="number"
								value={draft.quantityTo ?? ''}
								onChange={e => set('quantityTo', parseNum(e.target.value))}
								placeholder={t('products.filters.to')}
								aria-label={`${t('products.filters.quantityRange')} ${t('products.filters.to')}`}
							/>
						</div>
					</Section>

					{/* Release-date range */}
					<Section label={t('products.filters.releaseDateRange')}>
						<div className="grid grid-cols-2 gap-2">
							<DateInput
								value={isoToDate(draft.releaseDateFrom)}
								onChange={d => set('releaseDateFrom', dateToIso(d))}
								placeholder={t('products.filters.from')}
							/>
							<DateInput
								value={isoToDate(draft.releaseDateTo)}
								onChange={d => set('releaseDateTo', dateToIso(d))}
								placeholder={t('products.filters.to')}
							/>
						</div>
					</Section>
				</div>

				{/* Footer */}
				<div className="flex items-center justify-between gap-2 border-t border-border bg-background p-4">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={() => {
							setDraft({ ...EMPTY_FILTERS });
							onClear();
						}}>
						{t('products.filters.clear')}
					</Button>
					<Button type="button" size="sm" onClick={() => onApply(draft)}>
						{t('products.filters.apply')}
					</Button>
				</div>
			</SheetContent>
		</Sheet>
	);
}
